import Cookies from 'js-cookie';
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Trash2,
  Underline,
  Upload,
  X,
} from 'lucide-react';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useGetFirmAutoCcEmailsQuery } from '@/services/api/firmServicesActivationService';
import {
  useGetMatterDocumentsQuery,
  useGetMatterQuery,
} from '@/services/api/mattersService';
import {
  type ReadOrganizationWithEmployees,
  type ReadPersonWithRelationships,
  useAllPartiesWithRelationshipsQuery,
} from '@/services/api/partiesService';
import { useListFrontChannels } from '@/services/frontService';
import type { TemplateCategory } from '@/services/types/letters-types';

type CaseContact = {
  id: string;
  name: string;
  email: string;
  type: 'person' | 'organization';
};

/**
 * Extracts contacts with email addresses from case parties.
 */
function extractContactsWithEmails(
  persons: ReadPersonWithRelationships[],
  organizations: ReadOrganizationWithEmployees[],
): CaseContact[] {
  const personContacts = persons.flatMap((person) => {
    const emailHandles =
      person.contact_handles?.filter((h) => h.handle_type === 'EMAIL') || [];
    return emailHandles
      .filter((handle) => handle.value)
      .map((handle) => {
        const name =
          [person.first_name, person.last_name].filter(Boolean).join(' ') ||
          'Unknown Person';
        return {
          id: `${person.id}-${handle.id || handle.value}`,
          name,
          email: handle.value as string,
          type: 'person' as const,
        };
      });
  });

  const orgContacts = organizations.flatMap((org) => {
    const emailHandles =
      org.contact_handles?.filter((h) => h.handle_type === 'EMAIL') || [];
    return emailHandles
      .filter((handle) => handle.value)
      .map((handle) => {
        const name = org.legal_name || org.dba_name || 'Unknown Organization';
        return {
          id: `${org.id}-${handle.id || handle.value}`,
          name,
          email: handle.value as string,
          type: 'organization' as const,
        };
      });
  });

  return [...personContacts, ...orgContacts];
}

type SentFrontEmailResult = {
  conversation_id?: string;
  conversation_url?: string;
  message_id?: string;
};

export interface SendFrontEmailInlineCardRef {
  triggerSend: () => Promise<void>;
  isSending: boolean;
}

type BaseSendFrontEmailProps = {
  matterId: string;
  conversationId?: string | null;
  defaultTo?: string[];
  defaultSubject?: string;
  defaultBody?: string;
  channelId?: string | null; // selected Front channel (inbox)
  // Optional preselected matter files (by id + filename) to include inline
  // If provided, component will fetch blob data via download to attach.
  initialMatterFiles?: { id: string; filename: string }[];
  initialLorDocs?: { id: string; filename: string; label: string }[];
  artifactContext?: Record<string, unknown> | null;
  taskId?: string | null;
  deliverableId?: string | null;
  onSent?: (result: SentFrontEmailResult) => void;
  footerContent?: React.ReactNode;
  onPreviewDocument?: (doc: {
    id: string;
    filename: string;
    label: string;
  }) => void;
  /** Custom label for the send button */
  sendButtonLabel?: string;
  /** Whether an external action is in progress (disables the send button) */
  isExternalLoading?: boolean;
  /**
   * Custom header content for the email form.
   * If not provided, defaults to generic email messaging.
   */
  headerContent?: {
    /** Small label above the title (e.g., "Email insurer via Front") */
    subtitle?: string;
    /** Main title (e.g., "Send Letter of Representation") */
    title?: string;
    /** Description text below the title */
    description?: string;
    /** Subtitle shown for reply mode */
    replySubtitle?: string;
    /** Title shown for reply mode */
    replyTitle?: string;
  };
};

type DialogProps = BaseSendFrontEmailProps & {
  open: boolean;
  onClose: () => void;
  sidePanel?: React.ReactNode;
};

type FirmChannelStatus = 'idle' | 'loading' | 'ready' | 'missing' | 'error';

type SendFrontEmailFormProps = BaseSendFrontEmailProps & {
  isActive: boolean;
  variant: 'dialog' | 'inline';
  onClose?: () => void;
  sidePanel?: React.ReactNode;
  footerContent?: React.ReactNode;
};

// Simple email validation
const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const SendFrontEmailForm = forwardRef<
  SendFrontEmailInlineCardRef,
  SendFrontEmailFormProps
>(function SendFrontEmailForm(
  {
    matterId,
    conversationId,
    defaultTo = [],
    defaultSubject = '',
    defaultBody = '',
    channelId = null,
    initialMatterFiles = [],
    initialLorDocs = [],
    artifactContext = null,
    taskId = null,
    deliverableId = null,
    onSent,
    isActive,
    variant,
    onClose,
    sidePanel,
    footerContent,
    onPreviewDocument,
    sendButtonLabel,
    isExternalLoading = false,
    headerContent,
  },
  ref,
) {
  const [toEmails, setToEmails] = useState<string[]>([...defaultTo]);
  const [emailInput, setEmailInput] = useState('');
  const [isEmailInputFocused, setIsEmailInputFocused] = useState(false);
  const [subject, setSubject] = useState<string>(defaultSubject);
  const [body, setBody] = useState<string>(defaultBody);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);
  const [matterFiles, setMatterFiles] =
    useState<{ id: string; filename: string }[]>(initialMatterFiles);
  const [availableDocs, setAvailableDocs] = useState<
    { id: string; name: string; download_url?: string }[]
  >([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState<boolean>(false);
  const { channels } = useListFrontChannels(matterId);
  const { data: partiesData } = useAllPartiesWithRelationshipsQuery(
    { matterId },
    { skip: !isActive },
  );
  // Fetch matter data to get CMS email for auto-population
  const { data: matterData } = useGetMatterQuery(matterId, {
    skip: !isActive,
  });
  const matterCmsEmail = matterData?.cms_email ?? null;
  const firmId = matterData?.firm?.id ?? null;

  // Fetch firm's auto-CC emails from EMAIL_SETTINGS workflow config
  const { data: autoCcData } = useGetFirmAutoCcEmailsQuery(
    { firmId: firmId ?? '' },
    { skip: !isActive || !firmId },
  );

  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const caseContacts = useMemo(() => {
    if (!partiesData) return [];
    return extractContactsWithEmails(
      partiesData.persons || [],
      partiesData.organizations || [],
    );
  }, [partiesData]);
  const filteredCaseContacts = useMemo(() => {
    if (!contactSearchQuery.trim()) return caseContacts;
    const query = contactSearchQuery.toLowerCase();
    return caseContacts.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.email.toLowerCase().includes(query),
    );
  }, [caseContacts, contactSearchQuery]);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(
    channelId ?? null,
  );
  const [firmChannelStatus, setFirmChannelStatus] =
    useState<FirmChannelStatus>('idle');
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [previewDocument, setPreviewDocument] = useState<{
    id: string;
    filename: string;
    label: string;
  } | null>(null);

  const handlePreviewDocument = (doc: {
    id: string;
    filename: string;
    label: string;
  }) => {
    onPreviewDocument?.(doc);
  };
  const selectedChannel = useMemo(
    () => (channels || []).find((c) => c.id === selectedChannelId),
    [channels, selectedChannelId],
  );
  const isFirmChannelReady = firmChannelStatus === 'ready';
  const isFirmChannelLoading =
    firmChannelStatus === 'loading' || firmChannelStatus === 'idle';
  const isFirmChannelMissing = firmChannelStatus === 'missing';
  const isFirmChannelErrored = firmChannelStatus === 'error';
  let firmChannelFallbackMessage = 'Loading firm Front channel…';
  if (isFirmChannelErrored) {
    firmChannelFallbackMessage =
      "We couldn't load the firm Front channel. Refresh and try again.";
  } else if (isFirmChannelMissing) {
    firmChannelFallbackMessage =
      'Set your firm Front channel in Firm settings to send email.';
  }

  // Generated LOR documents (from Matter Documents service)
  const { data: matterDocuments = [] } = useGetMatterDocumentsQuery(matterId, {
    skip: !isActive,
  });
  const lorCategories = useMemo(
    () =>
      new Set<TemplateCategory>([
        'LOR_GENERIC',
        'LOR_FIRST_PARTY',
        'LOR_THIRD_PARTY',
        'LOR_HEALTH',
      ]),
    [],
  );
  const availableLorDocs = useMemo(
    () =>
      (matterDocuments || [])
        .filter(
          (d) =>
            !!d?.document_template?.category &&
            lorCategories.has(
              d.document_template.category as TemplateCategory,
            ),
        )
        .map((d) => ({
          id: String(d.id),
          name:
            (d.document_template?.name as string | undefined) ||
            'Letter of Representation',
          created_at: d.created_at,
        })),
    [matterDocuments, lorCategories],
  );
  const [lorQuery, setLorQuery] = useState<string>('');
  const filteredLorDocs = useMemo(
    () =>
      availableLorDocs.filter((d) =>
        (d.name || '').toLowerCase().includes(lorQuery.toLowerCase()),
      ),
    [availableLorDocs, lorQuery],
  );
  React.useEffect(() => {
    setSelectedChannelId(channelId ?? null);
  }, [channelId]);
  const [selectedLorDocs, setSelectedLorDocs] =
    useState<{ id: string; filename: string; label: string }[]>(initialLorDocs);

  const isReply = useMemo(() => !!conversationId, [conversationId]);
  const initializedRef = React.useRef(false);
  const inlineFileInputRef = useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (variant === 'inline' && defaultBody && !body) {
      setBody(defaultBody);
    }
  }, [variant, defaultBody, body]);

  // Initialize fields on first open for this session
  React.useEffect(() => {
    if (!isActive || initializedRef.current) return;
    setToEmails((prev) => (prev.length > 0 ? prev : [...defaultTo]));
    setSubject((prev) => prev || defaultSubject);
    setBody((prev) => prev || defaultBody);
    initializedRef.current = true;
  }, [isActive, defaultTo, defaultSubject, defaultBody]);

  // Sync toEmails when defaultTo changes (e.g., after async data loads)
  // Merge defaultTo emails with any existing emails (like CMS email)
  React.useEffect(() => {
    if (!isActive || defaultTo.length === 0) return;
    setToEmails((prev) => {
      // Merge defaultTo with existing emails, avoiding duplicates
      // Trim during comparison to avoid near-duplicates from whitespace
      const merged = [...prev];
      defaultTo.forEach((email) => {
        const trimmed = email.trim();
        if (
          !trimmed ||
          merged.some((e) => e.trim().toLowerCase() === trimmed.toLowerCase())
        ) {
          return;
        }
        // Add defaultTo emails at the beginning so they appear first
        merged.unshift(trimmed);
      });
      return merged;
    });
  }, [isActive, defaultTo]);

  // Reset init flag when dialog closes so next open re-initializes
  React.useEffect(() => {
    if (!isActive) {
      initializedRef.current = false;
    }
  }, [isActive]);

  // Auto-add CMS email to recipients when available (so user can see it)
  React.useEffect(() => {
    if (!isActive || !matterCmsEmail) return;
    const trimmedCms = matterCmsEmail.trim();
    if (!trimmedCms) return;
    setToEmails((prev) => {
      // Don't add if already present (trim to avoid whitespace near-duplicates)
      if (
        prev.some((e) => e.trim().toLowerCase() === trimmedCms.toLowerCase())
      ) {
        return prev;
      }
      return [...prev, trimmedCms];
    });
  }, [isActive, matterCmsEmail]);

  // Auto-add firm's auto-CC emails to recipients (from EMAIL_SETTINGS workflow)
  React.useEffect(() => {
    if (!isActive || !autoCcData?.auto_cc_emails?.length) return;
    setToEmails((prev) => {
      // Merge auto-CC emails with existing recipients (avoid duplicates)
      // Trim during comparison to avoid whitespace near-duplicates
      const merged = [...prev];
      autoCcData.auto_cc_emails.forEach((email) => {
        const trimmed = email.trim();
        if (
          !trimmed ||
          merged.some((e) => e.trim().toLowerCase() === trimmed.toLowerCase())
        ) {
          return;
        }
        merged.push(trimmed);
      });
      return merged;
    });
  }, [isActive, autoCcData]);

  // Fetch matter blobs only when dialog opens or matter changes
  React.useEffect(() => {
    if (!isActive) return;
    (async () => {
      try {
        setIsLoadingDocs(true);
        const res = await fetch(
          `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/blobs/`,
          { credentials: 'include' },
        );
        if (res.ok) {
          const data = (await res.json()) as
            | { results?: Array<Record<string, unknown>>; length?: number }
            | Array<Record<string, unknown>>;
          let list: Array<Record<string, unknown>> = [];
          if (Array.isArray(data)) {
            list = data;
          } else if (Array.isArray((data as { results?: unknown[] }).results)) {
            list = ((data as { results?: unknown[] }).results || []) as Array<
              Record<string, unknown>
            >;
          }
          const mapped = list.map((d) => {
            const rec = d as Record<string, unknown>;
            const id = String(rec.id ?? '');
            const name =
              (rec.name as string | undefined) ||
              (rec.original_filename as string | undefined) ||
              'File';
            return {
              id,
              name,
              original_filename: rec.original_filename as string | undefined,
              file_extension: rec.file_extension as string | undefined,
              download_url: rec.download_url as string | undefined,
            };
          });
          setAvailableDocs(mapped);
        }
      } finally {
        setIsLoadingDocs(false);
      }
    })();
  }, [isActive, matterId]);

  React.useEffect(() => {
    if (!isActive) return;
    setSelectedLorDocs(initialLorDocs);
  }, [initialLorDocs, isActive]);

  // Track if user has manually modified matter files to avoid overriding their selections
  const userModifiedMatterFilesRef = React.useRef(false);

  React.useEffect(() => {
    if (!isActive) {
      // Reset the flag when dialog closes so next open can sync
      userModifiedMatterFilesRef.current = false;
      return;
    }
    // Only sync from props if user hasn't manually modified the selection
    // This prevents parent re-renders from wiping out user's file selections
    if (!userModifiedMatterFilesRef.current) {
      setMatterFiles(initialMatterFiles);
    }
  }, [initialMatterFiles, isActive]);

  const handleSend = React.useCallback(async () => {
    if (!isReply && toEmails.length === 0) {
      toast.error('At least one recipient is required');
      return;
    }
    if (!subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    if (!body.trim()) {
      toast.error('Body is required');
      return;
    }

    try {
      setIsSending(true);
      if (!isReply && !selectedChannelId) {
        toast.error('Set the firm Front channel before sending.');
        return;
      }

      const inlineUploads = await Promise.all(
        files.map(async (f) => ({
          filename: f.name,
          base64: await fileToBase64(f),
        })),
      );
      const matterUploads = await Promise.all(
        matterFiles.map(async (mf) => {
          // Try blob download_url; if not present, fallback to API details then file URL
          let url = availableDocs.find((x) => x.id === mf.id)?.download_url;
          if (!url) {
            const detailsRes = await fetch(
              `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/blobs/${mf.id}/`,
              { credentials: 'include' },
            );
            if (detailsRes.ok) {
              const det = await detailsRes.json();
              url = det.download_url as string | undefined;
            }
          }
          if (!url) {
            throw new Error('Could not resolve blob download URL');
          }
          const blob = await fetch(url, { credentials: 'include' }).then((r) =>
            r.blob(),
          );
          const base64 = await blobToBase64(blob);
          return { filename: mf.filename, base64 };
        }),
      );
      // Download selected LoR docs and convert to base64 attachments
      const lorUploads = await Promise.all(
        selectedLorDocs.map(async (doc) => {
          const url = `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/documents/${doc.id}/preview/`;
          const response = await fetch(url, { credentials: 'include' });
          if (!response.ok) {
            throw new Error(`Failed to download LoR ${doc.label}`);
          }
          const blob = await response.blob();
          const header =
            response.headers.get('Content-Disposition') ||
            response.headers.get('content-disposition');
          const parsed = getFilenameFromContentDisposition(header);
          const filename = ensurePdfFilename(
            parsed || doc.filename || doc.label || 'Letter of Representation',
          );
          const base64 = await blobToBase64(blob);
          return { filename, base64 };
        }),
      );

      const attachments = [...inlineUploads, ...matterUploads, ...lorUploads];

      const payload: Record<string, unknown> = {
        to: isReply ? [] : toEmails,
        subject,
        body,
        channel_id: selectedChannelId ?? null,
        conversation_id: conversationId ?? null,
        attachments,
      };
      if (taskId) {
        payload.task_id = taskId;
      }
      if (deliverableId) {
        payload.deliverable_id = deliverableId;
      }
      if (artifactContext) {
        payload.artifact_context = artifactContext;
      }
      const res = await fetch(
        `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/front/send-email/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': Cookies.get('csrftoken') || '',
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Failed with status ${res.status}`);
      }
      const data: SentFrontEmailResult = await res.json();
      toast.success('Email sent via Front');
      onSent?.(data);
      onClose?.();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setIsSending(false);
    }
  }, [
    artifactContext,
    availableDocs,
    body,
    conversationId,
    deliverableId,
    files,
    isReply,
    matterId,
    matterFiles,
    onClose,
    onSent,
    selectedChannelId,
    selectedLorDocs,
    subject,
    taskId,
    toEmails,
  ]);

  // Expose triggerSend method via ref for external triggering
  useImperativeHandle(
    ref,
    () => ({
      triggerSend: handleSend,
      isSending,
    }),
    [handleSend, isSending],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const list = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...list]);
    e.currentTarget.value = '';
  };

  const removeFile = (name: string) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  };

  // Fetch firm Front channel to enforce From address
  React.useEffect(() => {
    if (!isActive || isReply) {
      return () => {};
    }
    let isMounted = true;
    if (!isActive || isReply) {
      return () => {
        isMounted = false;
      };
    }
    setFirmChannelStatus('loading');
    (async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/`,
          { credentials: 'include' },
        );
        if (!res.ok) {
          throw new Error('Failed to load matter');
        }
        const data = await res.json();
        if (!isMounted) return;
        const firmChannelId: string | null =
          (data?.firm?.front_channel?.front_id as string | undefined) || null;
        if (firmChannelId) {
          setSelectedChannelId(firmChannelId);
          setFirmChannelStatus('ready');
        } else {
          setSelectedChannelId(null);
          setFirmChannelStatus('missing');
        }
      } catch {
        if (!isMounted) return;
        setSelectedChannelId(null);
        setFirmChannelStatus('error');
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [isActive, matterId, isReply]);

  // Removed: backup channel auto-suggestion by fuzzy search

  const [fileQuery, setFileQuery] = useState<string>('');
  const filteredDocs = useMemo(
    () =>
      availableDocs.filter((d) =>
        (d.name || '').toLowerCase().includes(fileQuery.toLowerCase()),
      ),
    [availableDocs, fileQuery],
  );

  const resetToDefaults = () => {
    // Merge defaultTo with auto-CC emails on reset
    const merged = [...defaultTo];
    (autoCcData?.auto_cc_emails ?? []).forEach((email) => {
      if (!merged.some((e) => e.toLowerCase() === email.toLowerCase())) {
        merged.push(email);
      }
    });
    setToEmails(merged);
    setEmailInput('');
    setSubject(defaultSubject);
    setBody(defaultBody);
    setFiles([]);
    userModifiedMatterFilesRef.current = false;
    setMatterFiles(initialMatterFiles);
    setSelectedLorDocs(initialLorDocs);
  };

  // Email chip handlers
  const addEmail = (email: string) => {
    const trimmed = email.trim();
    if (trimmed && isValidEmail(trimmed) && !toEmails.includes(trimmed)) {
      setToEmails((prev) => [...prev, trimmed]);
      setEmailInput('');
    } else if (trimmed && !isValidEmail(trimmed)) {
      toast.error('Please enter a valid email address');
    }
  };

  const removeEmail = (emailToRemove: string) => {
    setToEmails((prev) => prev.filter((e) => e !== emailToRemove));
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      addEmail(emailInput);
    } else if (
      e.key === 'Backspace' &&
      emailInput === '' &&
      toEmails.length > 0
    ) {
      setToEmails((prev) => prev.slice(0, -1));
    }
  };

  const handleEmailPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const emails = pastedText.split(/[,;\s\n]+/).filter(Boolean);
    const validEmails = emails.filter(
      (email) => isValidEmail(email) && !toEmails.includes(email.trim()),
    );
    if (validEmails.length > 0) {
      setToEmails((prev) => [...prev, ...validEmails.map((em) => em.trim())]);
    }
  };

  const isInline = variant === 'inline';

  const actionBar = (
    <div
      className={`flex flex-wrap gap-2 pt-3 ${
        isInline ? 'justify-start' : 'justify-end border-t border-zinc-100'
      }`}
    >
      {isInline ? (
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={resetToDefaults}
        >
          Reset
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs"
          onClick={onClose}
          disabled={isSending}
        >
          Cancel
        </Button>
      )}
      <Button
        size="sm"
        className="h-8 text-xs"
        onClick={handleSend}
        disabled={
          isSending ||
          isExternalLoading ||
          (!isReply && firmChannelStatus !== 'ready')
        }
      >
        {isSending || isExternalLoading
          ? 'Sending...'
          : (sendButtonLabel ?? (isReply ? 'Reply' : 'Send'))}
      </Button>
    </div>
  );

  // Header content with generic defaults
  const resolvedHeader = {
    subtitle: headerContent?.subtitle ?? 'Send via Front',
    title: headerContent?.title ?? 'Compose Email',
    description:
      headerContent?.description ??
      (selectedChannel?.name
        ? `Messages sent from ${selectedChannel.name}.`
        : selectedChannel?.address
          ? `Messages sent from ${selectedChannel.address}.`
          : 'Messages sent from your firm Front channel.'),
    replySubtitle: headerContent?.replySubtitle ?? 'Reply via Front',
    replyTitle: headerContent?.replyTitle ?? 'Continue the conversation',
  };

  const composer = (
    <div className="flex flex-col gap-6">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          {isReply ? resolvedHeader.replySubtitle : resolvedHeader.subtitle}
        </p>
        <h3 className="text-xl font-semibold text-zinc-900">
          {isReply ? resolvedHeader.replyTitle : resolvedHeader.title}
        </h3>
        <p className="text-sm text-zinc-500">{resolvedHeader.description}</p>
      </div>

      {/* Channel status - only show errors/loading */}
      {!isReply && !isFirmChannelReady && (
        <div
          className={`rounded-lg px-2.5 py-1.5 text-xs ${
            isFirmChannelLoading
              ? 'border border-dashed border-zinc-300 bg-zinc-50 text-zinc-500'
              : isFirmChannelErrored
                ? 'border border-red-200 bg-red-50 text-red-700'
                : 'border border-amber-200 bg-amber-50 text-amber-700'
          }`}
        >
          {firmChannelFallbackMessage}
        </div>
      )}

      {/* Recipients */}
      {!isReply && (
        <div className="relative space-y-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            To
          </span>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className="flex min-h-9 flex-wrap items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2 py-1.5 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100 cursor-text"
            onClick={() => document.getElementById('email-chip-input')?.focus()}
          >
            {toEmails.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-800 border border-blue-200"
              >
                <span className="max-w-[160px] truncate">{email}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeEmail(email);
                  }}
                  className="rounded p-0.5 hover:bg-blue-100"
                  aria-label={`Remove ${email}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
            <input
              id="email-chip-input"
              type="email"
              value={emailInput}
              onChange={(e) => {
                setEmailInput(e.target.value);
                setContactSearchQuery(e.target.value);
              }}
              onKeyDown={handleEmailKeyDown}
              onFocus={() => setIsEmailInputFocused(true)}
              onBlur={() => {
                // Delay to allow click on dropdown items
                setTimeout(() => {
                  setIsEmailInputFocused(false);
                  if (emailInput.trim()) addEmail(emailInput);
                }, 150);
              }}
              onPaste={handleEmailPaste}
              placeholder={toEmails.length === 0 ? 'Add recipients...' : ''}
              className="min-w-[100px] flex-1 border-none bg-transparent py-0.5 text-xs font-medium text-zinc-800 outline-none placeholder:text-zinc-400"
              aria-label="Add email recipient"
            />
          </div>
          {/* Case contacts dropdown */}
          {isEmailInputFocused &&
            filteredCaseContacts.filter((c) => !toEmails.includes(c.email))
              .length > 0 && (
              <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-32 overflow-auto rounded border border-zinc-200 bg-white shadow-md">
                {filteredCaseContacts
                  .filter((c) => !toEmails.includes(c.email))
                  .map((contact) => (
                    <button
                      key={contact.id}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        addEmail(contact.email);
                        setEmailInput('');
                        setContactSearchQuery('');
                      }}
                      className="w-full px-2 py-1 text-left text-xs hover:bg-zinc-50"
                    >
                      <span className="font-medium">{contact.name}</span>
                      <span className="ml-1 text-zinc-400">
                        {contact.email}
                      </span>
                    </button>
                  ))}
              </div>
            )}
        </div>
      )}

      {/* Subject */}
      <div className="space-y-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          Subject
        </span>
        <Input
          id="email-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          aria-label="Email subject"
          className="h-9 rounded-lg border-zinc-200 text-sm font-medium text-zinc-800"
        />
      </div>

      <EmailBodyEditor value={body} onChange={setBody} />

      {/* Attachments Section */}
      <div className="space-y-3 pt-2 border-t border-zinc-100">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            Attachments
          </span>
          <button
            type="button"
            onClick={() => inlineFileInputRef.current?.click()}
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 hover:text-blue-700"
          >
            <Upload className="h-2.5 w-2.5" /> Upload
          </button>
        </div>
        <input
          ref={inlineFileInputRef}
          type="file"
          id="inline-files"
          className="sr-only"
          multiple
          onChange={handleFileChange}
        />

        {/* Uploaded files */}
        {files.length > 0 && (
          <div className="space-y-1">
            {files.map((f) => (
              <div
                key={f.name}
                className="flex items-center justify-between rounded-md bg-zinc-50 px-2 py-1.5 text-xs"
              >
                <span className="truncate font-medium text-zinc-800">
                  {f.name}
                </span>
                <button
                  type="button"
                  onClick={() => removeFile(f.name)}
                  className="text-zinc-400 hover:text-red-500"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Matter files */}
        {matterFiles.length > 0 && (
          <div className="space-y-1">
            {matterFiles.map((f) => (
              <div
                key={f.id}
                className="flex items-center justify-between rounded-md bg-blue-50 px-2 py-1.5 text-xs border border-blue-100"
              >
                <span className="truncate font-medium text-blue-800">
                  {f.filename}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    userModifiedMatterFilesRef.current = true;
                    setMatterFiles((prev) => prev.filter((x) => x.id !== f.id));
                  }}
                  className="text-zinc-400 hover:text-red-500"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 text-sm">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                Select matter files
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-2">
              <input
                id="matter-file-search"
                className="w-full rounded border px-2 py-2 text-sm"
                placeholder="Search files…"
                value={fileQuery}
                onChange={(e) => setFileQuery(e.target.value)}
                aria-label="Search matter files"
              />
              <div className="mt-2 max-h-48 overflow-auto rounded border">
                {(() => {
                  if (isLoadingDocs) {
                    return (
                      <div className="px-2 py-2 text-xs text-zinc-500">
                        Loading…
                      </div>
                    );
                  }
                  if (filteredDocs.length === 0) {
                    return (
                      <div className="px-2 py-2 text-xs text-zinc-500">
                        No files
                      </div>
                    );
                  }
                  return (
                    <ul className="divide-y">
                      {filteredDocs.map((d) => {
                        const isAdded = matterFiles.some(
                          (mf) => mf.id === d.id,
                        );
                        return (
                          <li key={d.id}>
                            <button
                              type="button"
                              onClick={() => {
                                const pickFilename = (doc: {
                                  name: string;
                                  original_filename?: string;
                                  file_extension?: string;
                                }) => {
                                  const base =
                                    doc.original_filename || doc.name;
                                  if (base && base.includes('.')) return base;
                                  if (doc.file_extension)
                                    return `${base}.${doc.file_extension}`;
                                  return base;
                                };
                                const filename = pickFilename(
                                  d as unknown as {
                                    name: string;
                                    original_filename?: string;
                                    file_extension?: string;
                                  },
                                );
                                userModifiedMatterFilesRef.current = true;
                                setMatterFiles((prev) =>
                                  isAdded
                                    ? prev.filter((x) => x.id !== d.id)
                                    : [...prev, { id: d.id, filename }],
                                );
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-50 ${
                                isAdded ? 'bg-zinc-100' : ''
                              }`}
                              aria-label={`${isAdded ? 'Remove' : 'Attach'} ${d.name}`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="truncate">{d.name}</span>
                                <span className="text-[10px] uppercase text-zinc-400">
                                  {d.download_url ? 'blob' : ''}
                                </span>
                              </div>
                              <div className="text-[11px] text-blue-600">
                                {isAdded ? 'Remove' : 'Attach'}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  );
                })()}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* LoR documents */}
        {selectedLorDocs.length > 0 && (
          <div className="space-y-1">
            {selectedLorDocs.map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between rounded-md bg-purple-50 px-2 py-1.5 text-xs border border-purple-100"
              >
                <button
                  type="button"
                  onClick={() => handlePreviewDocument(d)}
                  className="truncate font-medium text-purple-800 hover:underline"
                >
                  {d.label}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedLorDocs((prev) =>
                      prev.filter((x) => x.id !== d.id),
                    )
                  }
                  className="text-zinc-400 hover:text-red-500"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Quick add buttons */}
        <div className="flex items-center gap-2 pt-1">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-[10px] font-semibold text-zinc-600 hover:text-zinc-800 underline"
              >
                + Matter files
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-2">
              <input
                id="matter-file-search"
                className="w-full rounded-md border border-zinc-200 px-2 py-1.5 text-xs font-medium text-zinc-800"
                placeholder="Search files…"
                value={fileQuery}
                onChange={(e) => setFileQuery(e.target.value)}
                aria-label="Search matter files"
              />
              <div className="mt-1.5 max-h-40 overflow-auto">
                {isLoadingDocs ? (
                  <div className="px-2 py-2 text-[10px] text-zinc-400">
                    Loading…
                  </div>
                ) : filteredDocs.length === 0 ? (
                  <div className="px-2 py-2 text-[10px] text-zinc-400">
                    No files
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {filteredDocs.map((d) => {
                      const isAdded = matterFiles.some((mf) => mf.id === d.id);
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => {
                            const pickFilename = (doc: {
                              name: string;
                              original_filename?: string;
                              file_extension?: string;
                            }) => {
                              const base = doc.original_filename || doc.name;
                              if (base && base.includes('.')) return base;
                              if (doc.file_extension)
                                return `${base}.${doc.file_extension}`;
                              return base;
                            };
                            const filename = pickFilename(
                              d as unknown as {
                                name: string;
                                original_filename?: string;
                                file_extension?: string;
                              },
                            );
                            setMatterFiles((prev) =>
                              isAdded
                                ? prev.filter((x) => x.id !== d.id)
                                : [...prev, { id: d.id, filename }],
                            );
                          }}
                          className={`w-full px-2 py-1.5 text-left text-xs font-medium rounded-md hover:bg-zinc-50 ${
                            isAdded
                              ? 'bg-blue-50 text-blue-800'
                              : 'text-zinc-700'
                          }`}
                          aria-label={`${isAdded ? 'Remove' : 'Attach'} ${d.name}`}
                        >
                          <span className="truncate block">{d.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-[10px] font-semibold text-zinc-600 hover:text-zinc-800 underline"
              >
                + LoR documents
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-2">
              <input
                id="lor-doc-search"
                className="w-full rounded-md border border-zinc-200 px-2 py-1.5 text-xs font-medium text-zinc-800"
                placeholder="Search LoR documents…"
                value={lorQuery}
                onChange={(e) => setLorQuery(e.target.value)}
                aria-label="Search generated LoR documents"
              />
              <div className="mt-1.5 max-h-40 overflow-auto">
                {filteredLorDocs.length === 0 ? (
                  <div className="px-2 py-2 text-[10px] text-zinc-400">
                    No LoR documents
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {filteredLorDocs.map((d) => {
                      const isAdded = selectedLorDocs.some(
                        (x) => x.id === d.id,
                      );
                      const pickFilename = (doc: { name: string }) => {
                        const base = doc.name || 'Letter of Representation';
                        return base.endsWith('.pdf') || base.endsWith('.docx')
                          ? base
                          : `${base}.pdf`;
                      };
                      const filename = pickFilename({ name: d.name });
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() =>
                            setSelectedLorDocs((prev) =>
                              isAdded
                                ? prev.filter((x) => x.id !== d.id)
                                : [
                                    ...prev,
                                    {
                                      id: d.id,
                                      filename,
                                      label: d.name,
                                    },
                                  ],
                            )
                          }
                          className={`w-full px-2 py-1.5 text-left text-xs font-medium rounded-md hover:bg-zinc-50 ${
                            isAdded
                              ? 'bg-purple-50 text-purple-800'
                              : 'text-zinc-700'
                          }`}
                          aria-label={`${isAdded ? 'Remove' : 'Attach'} ${d.name}`}
                        >
                          <span className="truncate block">{d.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {actionBar}
    </div>
  );

  const formWithFooter = footerContent ? (
    <div className="space-y-4">
      {composer}
      <div className="rounded-lg border border-zinc-100 bg-zinc-50/50 p-3">
        {footerContent}
      </div>
    </div>
  ) : (
    composer
  );

  if (isInline) {
    return formWithFooter;
  }

  // If there's a side panel, use grid layout; otherwise just render the form
  if (sidePanel) {
    return (
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
        <div className="flex flex-col gap-4 px-4 py-4 min-w-[400px]">
          {formWithFooter}
        </div>
        <div className="rounded-lg border border-zinc-100 bg-zinc-50/80">
          {sidePanel}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 py-4 min-w-[400px]">
      {formWithFooter}
    </div>
  );
});

export const SendFrontEmailDialog: React.FC<DialogProps> = ({
  open,
  onClose,
  sidePanel,
  ...rest
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [previewDocument, setPreviewDocument] = useState<{
    id: string;
    filename: string;
    label: string;
  } | null>(null);
  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            onClose();
          }
        }}
      >
        <DialogContent className="w-auto max-w-[94vw] max-h-[85vh] border-none bg-transparent p-0 shadow-none overflow-hidden">
          <div className="max-h-[85vh] overflow-y-auto rounded-xl border border-zinc-200 bg-white shadow-xl">
            <SendFrontEmailForm
              {...rest}
              isActive={open}
              variant="dialog"
              onClose={onClose}
              sidePanel={sidePanel}
              onPreviewDocument={(doc) => {
                setPreviewDocument(doc);
                setPreviewModalOpen(true);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog
        open={previewModalOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setPreviewModalOpen(false);
            setPreviewDocument(null);
          }
        }}
      >
        <DialogContent className="w-[90vw] max-w-[1000px] max-h-[90vh]">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-4">
              <h3 className="text-lg font-semibold text-zinc-900">
                {previewDocument?.label || 'Document Preview'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setPreviewModalOpen(false);
                  setPreviewDocument(null);
                }}
                className="text-zinc-400 hover:text-zinc-600"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 p-6">
              {previewDocument && (
                <object
                  data={`/api/matters/${rest.matterId}/lor/${previewDocument.id}/download/`}
                  type="application/pdf"
                  className="w-full h-full min-h-[600px] rounded-lg border border-zinc-200"
                  title={previewDocument.label}
                >
                  <div className="flex items-center justify-center h-full text-zinc-500">
                    <p>
                      Unable to display PDF.{' '}
                      <a
                        href={`/api/matters/${rest.matterId}/lor/${previewDocument.id}/download/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Download instead
                      </a>
                    </p>
                  </div>
                </object>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

type InlineFormProps = BaseSendFrontEmailProps & {
  className?: string;
};

export const SendFrontEmailInlineCard = forwardRef<
  SendFrontEmailInlineCardRef,
  InlineFormProps
>(function SendFrontEmailInlineCard({ className = '', ...rest }, ref) {
  return (
    <div
      className={`rounded-2xl border border-zinc-100 bg-white p-6 shadow-lg shadow-zinc-200/60 ${className}`}
    >
      <SendFrontEmailForm ref={ref} {...rest} isActive variant="inline" />
    </div>
  );
});

export default SendFrontEmailDialog;

type EmailBodyEditorProps = {
  value: string;
  onChange: (next: string) => void;
};

const EmailBodyEditor: React.FC<EmailBodyEditorProps> = ({
  value,
  onChange,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!editorRef.current) return;
    if ((editorRef.current.innerHTML || '') !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const exec = (command: string, arg?: string) => {
    if (typeof document === 'undefined') return;
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    onChange(editorRef.current?.innerHTML ?? '');
  };

  const handleInput = () => {
    onChange(editorRef.current?.innerHTML ?? '');
  };

  const handleLink = () => {
    // eslint-disable-next-line no-alert
    const url = window.prompt('Enter link URL');
    if (!url) return;
    exec('createLink', url);
  };

  const toolbarButton = (
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
  ) => (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
      aria-label={label}
    >
      {icon}
    </button>
  );

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          Message
        </span>
        <div className="flex items-center gap-0.5">
          {toolbarButton(<Bold className="h-3 w-3" />, 'Bold', () =>
            exec('bold'),
          )}
          {toolbarButton(<Italic className="h-3 w-3" />, 'Italic', () =>
            exec('italic'),
          )}
          {toolbarButton(<Underline className="h-3 w-3" />, 'Underline', () =>
            exec('underline'),
          )}
          {toolbarButton(<List className="h-3 w-3" />, 'Bulleted list', () =>
            exec('insertUnorderedList'),
          )}
          {toolbarButton(
            <ListOrdered className="h-3 w-3" />,
            'Numbered list',
            () => exec('insertOrderedList'),
          )}
          {toolbarButton(
            <LinkIcon className="h-3 w-3" />,
            'Insert link',
            handleLink,
          )}
        </div>
      </div>
      <div
        ref={editorRef}
        className={`min-h-[120px] rounded-lg border border-zinc-200 bg-white px-2.5 py-2 text-xs font-medium text-zinc-800 ${
          isFocused ? 'border-blue-400 ring-1 ring-blue-100' : ''
        }`}
        contentEditable
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        data-placeholder="Write your email..."
        suppressContentEditableWarning
      />
    </div>
  );
};

async function fileToBase64(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

async function blobToBase64(blob: Blob): Promise<string> {
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function getFilenameFromContentDisposition(
  header: string | null,
): string | null {
  if (!header) return null;
  // Try RFC 5987 filename* first
  const starMatch = header.match(/filename\*=(?:UTF-8''|)([^;\n]+)/i);
  if (starMatch && starMatch[1]) {
    try {
      return decodeURIComponent(starMatch[1].replace(/^"|"$/g, ''));
    } catch {
      // fallthrough
    }
  }
  const match = header.match(/filename=([^;\n]+)/i);
  if (match && match[1]) {
    return match[1].trim().replace(/^"|"$/g, '');
  }
  return null;
}

function ensurePdfFilename(value: string): string {
  const trimmed = (value || '').trim() || 'Letter of Representation';
  if (trimmed.toLowerCase().endsWith('.pdf')) {
    return trimmed;
  }
  const withoutExt = trimmed.replace(/\.(docx?|rtf|txt)$/i, '');
  return `${withoutExt}.pdf`;
}
